import { useState, useEffect, useCallback } from 'react';
import { useEnhancedWebSocket } from './useEnhancedWebSocket';

/**
 * Investment Pool WebSocket Hook
 * Handles real-time investment pool performance and trading activity updates
 */
export const useInvestmentPoolWebSocket = (poolIds = [], options = {}) => {
  const {
    onPoolUpdate,
    onError,
    autoSubscribe = true
  } = options;

  const [poolData, setPoolData] = useState(new Map());
  const [subscribedPools, setSubscribedPools] = useState(new Set());
  const [lastUpdates, setLastUpdates] = useState(new Map());

  const {
    connectionState,
    isConnected,
    subscribeToPool,
    unsubscribeFromPool,
    setEventHandler,
    connect,
    disconnect
  } = useEnhancedWebSocket({
    autoConnect: true,
    reconnectAttempts: 5,
    reconnectInterval: 3000
  });

  /**
   * Handle pool updates from WebSocket
   */
  const handlePoolUpdate = useCallback((poolId, data) => {
    console.log(`🏦 Pool ${poolId} update received:`, data);
    
    setPoolData(prev => new Map(prev.set(poolId, data)));
    setLastUpdates(prev => new Map(prev.set(poolId, new Date())));
    
    // Call external handler if provided
    onPoolUpdate?.(poolId, data);
  }, [onPoolUpdate]);

  /**
   * Handle connection state changes
   */
  const handleConnectionChange = useCallback((state) => {
    console.log('🔌 Investment Pool WebSocket connection state:', state);
    
    if (state === 'AUTHENTICATED' && autoSubscribe) {
      // Auto-subscribe to all pools when connection is established
      setTimeout(() => {
        poolIds.forEach(poolId => {
          if (!subscribedPools.has(poolId)) {
            subscribeToPoolUpdates(poolId);
          }
        });
      }, 1000); // Small delay to ensure authentication is complete
    } else if (state === 'CLOSED') {
      setSubscribedPools(new Set());
    }
  }, [autoSubscribe, poolIds, subscribedPools]);

  /**
   * Handle WebSocket errors
   */
  const handleError = useCallback((error) => {
    console.error('❌ Investment Pool WebSocket error:', error);
    onError?.(error);
  }, [onError]);

  /**
   * Subscribe to updates for a specific pool
   */
  const subscribeToPoolUpdates = useCallback((poolId) => {
    if (isConnected && !subscribedPools.has(poolId)) {
      const success = subscribeToPool(poolId);
      if (success) {
        setSubscribedPools(prev => new Set([...prev, poolId]));
        console.log(`✅ Subscribed to pool ${poolId} updates`);
      }
      return success;
    }
    return false;
  }, [isConnected, subscribedPools, subscribeToPool]);

  /**
   * Unsubscribe from updates for a specific pool
   */
  const unsubscribeFromPoolUpdates = useCallback((poolId) => {
    if (subscribedPools.has(poolId)) {
      const success = unsubscribeFromPool(poolId);
      if (success) {
        setSubscribedPools(prev => {
          const newSet = new Set(prev);
          newSet.delete(poolId);
          return newSet;
        });
        setPoolData(prev => {
          const newMap = new Map(prev);
          newMap.delete(poolId);
          return newMap;
        });
        setLastUpdates(prev => {
          const newMap = new Map(prev);
          newMap.delete(poolId);
          return newMap;
        });
        console.log(`❌ Unsubscribed from pool ${poolId} updates`);
      }
      return success;
    }
    return false;
  }, [subscribedPools, unsubscribeFromPool]);

  /**
   * Subscribe to multiple pools at once
   */
  const subscribeToMultiplePools = useCallback((poolIdArray) => {
    const results = poolIdArray.map(poolId => subscribeToPoolUpdates(poolId));
    return results.every(result => result);
  }, [subscribeToPoolUpdates]);

  /**
   * Unsubscribe from multiple pools at once
   */
  const unsubscribeFromMultiplePools = useCallback((poolIdArray) => {
    const results = poolIdArray.map(poolId => unsubscribeFromPoolUpdates(poolId));
    return results.every(result => result);
  }, [unsubscribeFromPoolUpdates]);

  /**
   * Unsubscribe from all pools
   */
  const unsubscribeFromAllPools = useCallback(() => {
    const poolIdArray = Array.from(subscribedPools);
    return unsubscribeFromMultiplePools(poolIdArray);
  }, [subscribedPools, unsubscribeFromMultiplePools]);

  /**
   * Get data for a specific pool
   */
  const getPoolData = useCallback((poolId) => {
    return poolData.get(poolId) || null;
  }, [poolData]);

  /**
   * Get last update time for a specific pool
   */
  const getLastUpdate = useCallback((poolId) => {
    return lastUpdates.get(poolId) || null;
  }, [lastUpdates]);

  /**
   * Check if a pool is subscribed
   */
  const isPoolSubscribed = useCallback((poolId) => {
    return subscribedPools.has(poolId);
  }, [subscribedPools]);

  /**
   * Refresh data for a specific pool
   */
  const refreshPool = useCallback((poolId) => {
    if (isConnected && subscribedPools.has(poolId)) {
      // Unsubscribe and resubscribe to trigger fresh data
      unsubscribeFromPoolUpdates(poolId);
      setTimeout(() => {
        subscribeToPoolUpdates(poolId);
      }, 500);
    }
  }, [isConnected, subscribedPools, subscribeToPoolUpdates, unsubscribeFromPoolUpdates]);

  /**
   * Refresh all subscribed pools
   */
  const refreshAllPools = useCallback(() => {
    const poolIdArray = Array.from(subscribedPools);
    poolIdArray.forEach(poolId => refreshPool(poolId));
  }, [subscribedPools, refreshPool]);

  // Set up event handlers
  useEffect(() => {
    setEventHandler('onPoolUpdate', handlePoolUpdate);
    setEventHandler('onConnectionChange', handleConnectionChange);
    setEventHandler('onError', handleError);
  }, [setEventHandler, handlePoolUpdate, handleConnectionChange, handleError]);

  // Auto-subscribe to pools when connected
  useEffect(() => {
    if (isConnected && autoSubscribe) {
      poolIds.forEach(poolId => {
        if (!subscribedPools.has(poolId)) {
          subscribeToPoolUpdates(poolId);
        }
      });
    }
  }, [isConnected, autoSubscribe, poolIds, subscribedPools, subscribeToPoolUpdates]);

  // Handle poolIds changes
  useEffect(() => {
    if (isConnected && autoSubscribe) {
      // Subscribe to new pools
      poolIds.forEach(poolId => {
        if (!subscribedPools.has(poolId)) {
          subscribeToPoolUpdates(poolId);
        }
      });

      // Unsubscribe from pools no longer in the list
      subscribedPools.forEach(poolId => {
        if (!poolIds.includes(poolId)) {
          unsubscribeFromPoolUpdates(poolId);
        }
      });
    }
  }, [poolIds, isConnected, autoSubscribe, subscribedPools, subscribeToPoolUpdates, unsubscribeFromPoolUpdates]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unsubscribeFromAllPools();
    };
  }, [unsubscribeFromAllPools]);

  return {
    // Pool data
    poolData: Object.fromEntries(poolData),
    lastUpdates: Object.fromEntries(lastUpdates),
    
    // Connection state
    isConnected,
    subscribedPools: Array.from(subscribedPools),
    connectionState,
    
    // Control methods
    subscribeToPoolUpdates,
    unsubscribeFromPoolUpdates,
    subscribeToMultiplePools,
    unsubscribeFromMultiplePools,
    unsubscribeFromAllPools,
    refreshPool,
    refreshAllPools,
    connect,
    disconnect,
    
    // Utility methods
    getPoolData,
    getLastUpdate,
    isPoolSubscribed,
    hasPoolData: (poolId) => poolData.has(poolId),
    isPoolLoading: (poolId) => isConnected && !poolData.has(poolId) && subscribedPools.has(poolId),
    
    // Aggregate data
    totalSubscribedPools: subscribedPools.size,
    allPoolsData: Array.from(poolData.values()),
    
    // Performance shortcuts for subscribed pools
    getPoolPerformance: (poolId) => getPoolData(poolId)?.performance || {},
    getPoolSharePrice: (poolId) => getPoolData(poolId)?.sharePrice || 0,
    getPoolTotalValue: (poolId) => getPoolData(poolId)?.totalValue || 0,
    getPoolDailyReturn: (poolId) => getPoolData(poolId)?.dailyReturn || 0
  };
};

export default useInvestmentPoolWebSocket;